import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaHeart, FaAppleAlt, FaRunning, FaMoon, FaFlask, FaCalendarAlt, FaBaby, FaBurn, FaBrain, FaBalanceScale, FaSmile, FaShieldAlt, FaSun, FaUserShield, FaSeedling, FaHome } from "react-icons/fa";
import HealthImg from "../assets/images/Health.avif";
import reproductiveImg from "../assets/images/reproductive.avif";
import mentalImg from "../assets/images/mental.webp";
import preventiveImg from "../assets/images/preventive.avif";
import { FiHome } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

const WellnessTips = () => {
  const [activeAccordion, setActiveAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const wellnessCategories = [
    {
      title: "Physical Health",
      icon: <FaHeart className="text-pink-500 text-2xl" />,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-pink-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaAppleAlt className="text-pink-600 mr-2" />
              <h4 className="font-semibold">Nutrition</h4>
            </div>
            <p className="text-sm">
              Eat a rainbow of fruits/vegetables daily. Include iron-rich foods (spinach, lentils) and calcium sources (dairy, almonds). 
              <a href="https://www.womenshealth.gov/healthy-eating" target="_blank" rel="noopener noreferrer" className="text-purple-600 ml-1">Learn more</a>
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaRunning className="text-green-600 mr-2" />
              <h4 className="font-semibold">Exercise</h4>
            </div>
            <p className="text-sm">
              150 mins moderate exercise weekly. Try weight-bearing exercises for bone health. Pelvic floor exercises benefit all ages.
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaMoon className="text-purple-600 mr-2" />
              <h4 className="font-semibold">Sleep</h4>
            </div>
            <p className="text-sm">
              Maintain consistent sleep schedule. Limit screen time before bed. Women need 7-9 hours for optimal hormone regulation.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaFlask className="text-blue-600 mr-2" />
              <h4 className="font-semibold">Checkups</h4>
            </div>
            <p className="text-sm">
              Annual well-woman exams. Mammograms starting at 40-50. Bone density tests at 65 (earlier if high risk).
            </p>
          </div>
        </div>
      ),
      image: HealthImg
    },
    {
      title: "Reproductive Health",
      icon: <FaCalendarAlt className="text-purple-500 text-2xl" />,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-pink-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaCalendarAlt className="text-pink-600 mr-2" />
              <h4 className="font-semibold">Menstrual Health</h4>
            </div>
            <p className="text-sm">
              Track cycles for patterns. Use period apps. Change tampons every 4-8 hours. Severe pain may indicate conditions like endometriosis.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaBaby className="text-green-600 mr-2" />
              <h4 className="font-semibold">Pregnancy & Fertility</h4>
            </div>
            <p className="text-sm">
              Take 400mcg folic acid pre-conception. Fertility peaks late 20s. Regular ovulation tracking helps identify fertile window.
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaBurn className="text-yellow-600 mr-2" />
              <h4 className="font-semibold">Menopause</h4>
            </div>
            <p className="text-sm">
              Manage hot flashes with layered clothing. Vaginal moisturizers help dryness. Increase calcium/vitamin D for bone health.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaShieldAlt className="text-blue-600 mr-2" />
              <h4 className="font-semibold">Safe Sex</h4>
            </div>
            <p className="text-sm">
              Condoms prevent STIs. Consider LARCs (IUDs/implants) for long-term protection. Regular STI testing if sexually active.
            </p>
          </div>
        </div>
      ),
      image: reproductiveImg 
    },
    {
      title: "Mental Wellness",
      icon: <FaBrain className="text-blue-500 text-2xl" />,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-purple-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaBalanceScale className="text-purple-600 mr-2" />
              <h4 className="font-semibold">Stress Management</h4>
            </div>
            <p className="text-sm">
              Try 4-7-8 breathing (inhale 4s, hold 7s, exhale 8s). Journaling reduces anxiety. Even 10-minute meditation helps.
            </p>
          </div>
          <div className="bg-pink-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaBrain className="text-pink-600 mr-2" />
              <h4 className="font-semibold">Therapy</h4>
            </div>
            <p className="text-sm">
              Postpartum depression affects 1 in 7 women. CBT helps with anxiety. Support groups normalize shared experiences.
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaBalanceScale className="text-green-600 mr-2" />
              <h4 className="font-semibold">Work-Life Balance</h4>
            </div>
            <p className="text-sm">
              Set "no work" time blocks. Delegate household tasks. Learn to say no without guilt. Schedule "me time".
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaSmile className="text-blue-600 mr-2" />
              <h4 className="font-semibold">Self-Esteem</h4>
            </div>
            <p className="text-sm">
              Unfollow social media that triggers comparison. Practice body neutrality. Celebrate non-appearance achievements.
            </p>
          </div>
        </div>
      ),
      image: mentalImg
    },
    {
      title: "Preventive Care",
      icon: <FaShieldAlt className="text-green-500 text-2xl" />,
      content: (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-yellow-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaShieldAlt className="text-yellow-600 mr-2" />
              <h4 className="font-semibold">Vaccinations</h4>
            </div>
            <p className="text-sm">
              HPV vaccine up to age 45. Annual flu shot. Tdap booster every 10 years. Shingles vaccine at 50+.
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaSeedling className="text-purple-600 mr-2" />
              <h4 className="font-semibold">Healthy Habits</h4>
            </div>
            <p className="text-sm">
              Limit alcohol to 1 drink/day. Stay hydrated (2.7L daily). Quit smoking - women face higher lung cancer risks.
            </p>
          </div>
          <div className="bg-pink-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaSun className="text-pink-600 mr-2" />
              <h4 className="font-semibold">Skin Care</h4>
            </div>
            <p className="text-sm">
              Daily SPF 30+ prevents 80% of aging. Check moles ABCDE rule. Hormonal changes may cause adult acne.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center mb-2">
              <FaUserShield className="text-blue-600 mr-2" />
              <h4 className="font-semibold">Safety</h4>
            </div>
            <p className="text-sm">
              Know intimate partner violence resources (800-799-7233). Share live location with trusted contacts when meeting new people.
            </p>
          </div>
        </div>
      ),
      image: preventiveImg
    }
  ];

  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8">
<Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Women's Health Tips - FlowHearts | Holistic Wellness Guide</title>
    <meta
      name="description"
      content="Discover FlowHearts' Women's Health Tips for holistic wellness, covering physical, reproductive, mental, and preventive care for all life stages."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/wellness-tips" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/wellness-tips" />
    <meta
      property="og:title"
      content="Women's Health Tips - FlowHearts | Holistic Wellness Guide"
    />
    <meta
      property="og:description"
      content="Explore FlowHearts' holistic women's health tips, offering guidance on physical, reproductive, mental, and preventive care for every stage of life."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/wellness-tips-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/wellness-tips" />
    <meta
      property="twitter:title"
      content="Women's Health Tips - FlowHearts | Holistic Wellness Guide"
    />
    <meta
      property="twitter:description"
      content="Get holistic women's health tips from FlowHearts, with practical advice on physical, reproductive, mental, and preventive wellness."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/wellness-tips-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Women's Health Tips",
          "url": "https://flowhearts.com/wellness-tips",
          "description": "FlowHearts' Women's Health Tips provide holistic guidance on physical, reproductive, mental, and preventive care for all life stages.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-4 md:px-6">
        {/* Back to Home Button */}
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>

        <header className="text-center mb-8">
          <h2 className="text-4xl font-cormorant font-bold text-purple-800 mb-2">Women's Wellness Guide</h2>
          <p className="text-pink-600">Holistic health tips for every life stage</p>
        </header>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-pink-100 p-4 rounded-xl text-center">
            <FaHeart className="text-pink-600 text-3xl mx-auto mb-2" />
            <p className="font-bold">77%</p>
            <p className="text-sm">Women prioritize family health over their own</p>
          </div>
          <div className="bg-green-100 p-4 rounded-xl text-center">
            <FaRunning className="text-green-600 text-3xl mx-auto mb-2" />
            <p className="font-bold">150 min</p>
            <p className="text-sm">Recommended weekly exercise</p>
          </div>
          <div className="bg-purple-100 p-4 rounded-xl text-center">
            <FaMoon className="text-purple-600 text-3xl mx-auto mb-2" />
            <p className="font-bold">7-9 hrs</p>
            <p className="text-sm">Optimal sleep for hormone balance</p>
          </div>
          <div className="bg-blue-100 p-4 rounded-xl text-center">
            <FaFlask className="text-blue-600 text-3xl mx-auto mb-2" />
            <p className="font-bold">1 in 8</p>
            <p className="text-sm">Women will develop breast cancer</p>
          </div>
        </div>

        {/* Wellness Accordions */}
        <div className="space-y-4 mb-8">
          {wellnessCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
              <button
                className="w-full p-4 flex items-center justify-between text-left"
                onClick={() => toggleAccordion(index)}
              >
                <div className="flex items-center">
                  {category.icon}
                  <h3 className="ml-3 text-lg font-semibold text-purple-800">{category.title}</h3>
                </div>
                <svg
                  className={`w-5 h-5 text-purple-600 transform transition-transform ${
                    activeAccordion === index ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {activeAccordion === index && (
                <div className="p-4 border-t border-gray-100">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="md:w-2/3">
                      {category.content}
                    </div>
                    <div className="md:w-1/3">
                      <img 
                        src={category.image} 
                        alt={category.title}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2">Did You Know?</h4>
                        <p className="text-sm">
                          {index === 0 && "Women metabolize alcohol differently than men, becoming impaired faster."}
                          {index === 1 && "PCOS affects 1 in 10 women but often goes undiagnosed for years."}
                          {index === 2 && "Women are twice as likely as men to experience anxiety disorders."}
                          {index === 3 && "Heart disease is the #1 killer of women - more than all cancers combined."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Age-Specific Tips */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <h2 className="text-xl font-bold text-purple-800 mb-4">Age-Specific Wellness</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-pink-50 p-4 rounded-lg">
              <h3 className="font-semibold text-pink-700 mb-2 flex items-center">
                <FaSeedling className="mr-2" /> Teens
              </h3>
              <ul className="text-sm space-y-1">
                <li>• Puberty education</li>
                <li>• Acne care</li>
                <li>• Menstrual hygiene</li>
                <li>• Body positivity</li>
              </ul>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="font-semibold text-purple-700 mb-2 flex items-center">
                <FaAppleAlt className="mr-2" /> 20s-30s
              </h3>
              <ul className="text-sm space-y-1">
                <li>• Fertility awareness</li>
                <li>• Career health</li>
                <li>• Fitness foundations</li>
                <li>• Financial wellness</li>
              </ul>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="font-semibold text-green-700 mb-2 flex items-center">
                <FaBurn className="mr-2" /> 40s-50s
              </h3>
              <ul className="text-sm space-y-1">
                <li>• Hormonal changes</li>
                <li>• Mammograms</li>
                <li>• Bone health</li>
                <li>• Stress management</li>
              </ul>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-700 mb-2 flex items-center">
                <FaHeart className="mr-2" /> 60+
              </h3>
              <ul className="text-sm space-y-1">
                <li>• Fall prevention</li>
                <li>• Memory care</li>
                <li>• Heart health</li>
                <li>• Social connection</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl shadow-sm border border-purple-100">
          <h3 className="text-lg font-semibold text-purple-800 mb-3">Trusted Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="https://www.womenshealth.gov/" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg hover:shadow-md transition-all flex items-center">
              <img src="https://media.istockphoto.com/id/1369740341/vector/cervical-cancer-world-cancer-awareness-day-women-health.jpg?s=612x612&w=0&k=20&c=0ozSllJKfUllLM12YjgHMc-h219mbjLSqcqZIb6KBcM=" alt="WomensHealth.gov" className="h-8 mr-3" />
              <span className="text-sm font-medium">U.S. Women's Health</span>
            </a>
            <a href="https://www.acog.org/" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg hover:shadow-md transition-all flex items-center">
              <img src="https://media.istockphoto.com/id/1423545951/vector/routine-monitoring-of-maternal-and-fetal-health.jpg?s=612x612&w=0&k=20&c=UnR1yWBkTxBcgdcKyz0sAs50KlPH7SFpw2uC9S3zr7k=" alt="ACOG" className="h-8 mr-3" />
              <span className="text-sm font-medium">OB-GYN Guidelines</span>
            </a>
            <a href="https://www.cdc.gov/women/" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg hover:shadow-md transition-all flex items-center">
              <img src="https://static.vecteezy.com/system/resources/previews/012/889/070/non_2x/self-love-concept-the-girl-admires-herself-in-the-mirror-mental-health-cartoon-illustration-vector.jpg" alt="CDC" className="h-8 mr-3" />
              <span className="text-sm font-medium">CDC Women's Health</span>
            </a>
          </div>
        </div>

        {/* Educational Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-8">
          {/* How to Start Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaShieldAlt className="mr-3 text-pink-500" />
              How to Start Your Wellness Journey
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Assess Your Health
                </h3>
                <p className="text-gray-700 text-center">
                  Begin with a self-check: track energy levels and note any concerns
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Set Realistic Goals
                </h3>
                <p className="text-gray-700 text-center">
                  Focus on small, sustainable changes in daily routines
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-green-700 mb-2">
                  Build Support
                </h3>
                <p className="text-gray-700 text-center">
                  Connect with communities for motivation and shared experiences
                </p>
              </div>
            </div>
          </div>

          {/* The Science Behind */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaBrain className="mr-3 text-purple-500" />
              Science of Women's Wellness
            </h2>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Key Health Factors
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">
                    Hormone Balance
                  </p>
                  <p className="text-gray-700">
                    Natural fluctuations affect energy, mood, and overall vitality
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Nutrition Impact
                  </p>
                  <p className="text-gray-700">
                    Essential nutrients support long-term health and prevention
                  </p>
                </div>
              </div>
              <p className="text-gray-700">
                Recent studies highlight how lifestyle choices influence women's health outcomes across life stages.
              </p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSmile className="mr-3 text-green-500" />
              Benefits of Holistic Self-Care
            </h2>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <p className="text-gray-700 mb-4">
                Embracing comprehensive women's health tips can lead to improved quality of life and empowerment.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Better energy management through mindful practices</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Stronger resilience to daily stresses</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Enhanced body awareness and confidence</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Improved relationships through self-understanding</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Myths Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSun className="mr-3 text-red-500" />
              Debunking Common Wellness Myths
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Self-Care Is Selfish
                </h3>
                <p className="text-gray-700">
                  Reality: Prioritizing health enables better support for others
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Wellness Is Expensive
                </h3>
                <p className="text-gray-700">
                  Reality: Many effective practices are free or low-cost
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: One Size Fits All
                </h3>
                <p className="text-gray-700">
                  Reality: Personalization is key in women's health
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Age Defines Health
                </h3>
                <p className="text-gray-700">
                  Reality: Vitality possible at every stage with proper care
                </p>
              </div>
            </div>
          </div>

          {/* Tips Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSeedling className="mr-3 text-blue-500" />
              Daily Wellness Practices
            </h2>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <p className="text-gray-700 mb-4">
                Incorporate these simple habits for sustainable women's wellness.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Morning Routine</h3>
                  <p className="text-gray-700 text-sm">
                    Start with hydration and gentle stretching
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Mindful Eating</h3>
                  <p className="text-gray-700 text-sm">
                    Choose nutrient-dense meals mindfully
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Evening Wind-Down</h3>
                  <p className="text-gray-700 text-sm">
                    Practice gratitude journaling
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Weekly Check-In</h3>
                  <p className="text-gray-700 text-sm">
                    Review progress and adjust as needed
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Help Section */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaUserShield className="mr-3 text-yellow-500" />
              When to Seek Support
            </h2>
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <p className="text-gray-700 mb-4">
                Listen to your body and seek help when needed for optimal health.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaShieldAlt className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Persistent unusual symptoms</span>
                </li>
                <li className="flex items-start">
                  <FaShieldAlt className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Changes in mood or energy</span>
                </li>
                <li className="flex items-start">
                  <FaShieldAlt className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Family history concerns</span>
                </li>
                <li className="flex items-start">
                  <FaShieldAlt className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Lifestyle transition periods</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Professional guidance complements personal wellness efforts.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WellnessTips;